<script setup lang="ts">
import { useIonRouter } from '@ionic/vue'
import { inject, onMounted, onUpdated, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useElementSize } from '@vueuse/core'
import { SafeArea } from 'capacitor-plugin-safe-area'
import ShareButton from './ShareButton.vue'
import FavouriteButton from './FavouriteButton.vue'
import Logo from '@/assets/sekai_storyline.png'
import type { Chat, Message, StarOperation } from '@/types'
import { useChatsStore } from '@/store/chats'
import { useKeyboardStore } from '@/store/keyboard'
import { useUserStore } from '@/store/user'
import type { CreatedEventModel } from '@/types/universe'
import { useGlobalStateStore } from '@/store/globalState'
import { usePreferencesStore } from '@/store/preferences'
import { useShareStore } from '@/store/shareStore'
import { autoPlayKey } from '@/constant/symbol'
import type { PreviousUniverse } from '@/services/universe'
import { universeService } from '@/services/universe'
import StoryLineModal from '@/components/StoryLineModal.vue'
import UpArrowIcon from '@/components/svg/UpArrowIcon.vue'
import DownloadSekaiModal from '@/components/DownloadSekaiModal.vue'
import { convertInitMessagesToMessages } from '@/utils/generateChatFromUniverse'

interface Props {
  chat: Chat | undefined
  personCountIsHidden?: boolean
  inviteIsHidden?: boolean
  disabled?: boolean
  chatRoundEnd?: boolean
  isPreview: boolean
  messages: Message[]
}

interface Emits {
  (e: 'click:star', operation: StarOperation): void
  (e: 'sendMessage', message: string): void
  (e: 'updateInputHeight', height: number): void
  (e: 'updateEvent', event: CreatedEventModel): void
  (e: 'onToggleDecorations', show: boolean): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const router = useIonRouter()
const keyboardStore = useKeyboardStore()
const chatsStore = useChatsStore()
const userStore = useUserStore()
const globalStore = useGlobalStateStore()
const shareStore = useShareStore()
const previousUniverse = ref<PreviousUniverse | null>(null)
const nextUniverse = ref<PreviousUniverse | null>(null)
const isStorylineModalOpen = ref(false)
const downloadSekaiModalTitle = ref('')

async function loadPreviousAndNextUniverse(universeId: number) {
  const pre = await universeService.getPreviousUniverse(universeId)
  previousUniverse.value = pre.data
  const next = await universeService.getNextUniverse(universeId)
  nextUniverse.value = next.data
}

const { logoutChatInput } = storeToRefs(chatsStore)
// the user input value
const userInput = ref('')
// the user input area UI dom
const inputRef = ref()
// the full area dom UI
const messageAreaEl = ref<HTMLElement | null>(null)
const { height: messageAreaHeight } = useElementSize(messageAreaEl)
const isDownloadModalOpen = ref(false)

function dismissModal() {
  isDownloadModalOpen.value = false
}

// This variable control the suggest response page show or not
const showEventSuggestions = ref<boolean>(false)
const autoplay = inject(autoPlayKey)
const manuallyAutoplay = ref(false)
// When the page updates, check the logoutChatInput value
// If it is not empty, assign to userInput
// If it is empty, do nothing
onMounted(async () => {
  if (logoutChatInput.value !== '') {
    userInput.value = logoutChatInput.value
    logoutChatInput.value = ''
  }
  const universeId = props.chat?.universe.universeId
  if (universeId) {
    loadPreviousAndNextUniverse(props.chat?.universe.universeId)
  }
})

onUpdated(() => {
  if (logoutChatInput.value !== '') {
    userInput.value = logoutChatInput.value
    logoutChatInput.value = ''
  }
})

watch(
  () => messageAreaHeight.value,
  async (newHeight) => {
    const safeInsets = await SafeArea.getSafeAreaInsets()
    emit('updateInputHeight', newHeight + safeInsets.insets.bottom)
  },
)

watch(
  () => showEventSuggestions.value,
  (val) => {
    globalStore.setDisableSwipe(val)
    emit('onToggleDecorations', !val)
  },
)

function showScenarioSheet() {
  isDownloadModalOpen.value = true
  downloadSekaiModalTitle.value = 'Wonder what happens next?'
}

watch(
  () => props.chatRoundEnd,
  (newVal) => {
    console.debug(`chatRoundEnd: ${newVal}`)
    const lastEvent = (props.chat?.universe.events.length || 0) >= 4
    if (lastEvent) {
      showEventSuggestions.value = false
      return
    }
    if (newVal && chatsStore.currentChatId === props.chat?.chatId) {
      showScenarioSheet()
    }
  },
)

defineExpose({
  showScenarioSheet,
})

watch(
  () => userStore.isLoggedIn,
  (newVal) => {
    if (
      newVal
      && props.chatRoundEnd
      && props.chat?.chatId === chatsStore.currentChatId
    ) {
      showEventSuggestions.value = true
    }
  },
)

const showTipBubble = ref(false)
const preferenceStore = usePreferencesStore()
watch(
  () => preferenceStore.preferencesAreLoaded,
  (newVal) => {
    if (newVal) {
      if (preferenceStore.showedReplyTip) {
        return
      }
      setTimeout(() => {
        showTipBubble.value = true
        preferenceStore.showedReplyTip = true
        setTimeout(() => {
          showTipBubble.value = false
        }, 5000)
      }, 3000)
    }
  },
)

onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search)
  const isShared = urlParams.get('__share__') === 'true'
  if (isShared && props.chat?.universe) {
    shareStore.setSharingInfo(
      props.chat!,
      convertInitMessagesToMessages(props.chat?.universe),
    )
    router.push(`/select-msg${window.location.search}`)
  }
})
</script>

<template>
  <div class="flex-shrink-0 gradient-bg z-100">
    <div ref="messageAreaEl" class="relative z-200">
      <div class="flex justify-between items-center">
        <div class="flex items-center gap-2 ml-4">
          <!-- <img style="width: 24px; height: 24px; border-radius: 100%;" :src="chat?.universe.creatorAvatarUrl" /> -->
          <div class="creator-name">
            @{{ chat?.universe.creatorName }}
          </div>
        </div>
        <div class="flex items-center pr-1" @click="isDownloadModalOpen = true">
          <favourite-button
            v-if="!isPreview && !keyboardStore.isTyping" :chat="chat!"
            :is-favorite="chat?.isStared || false"
          />
          <share-button
            v-if="!isPreview && !keyboardStore.isTyping" :chat="chat!" @on-create-video="() => {
              // shareStore.setSharingInfo(
              //   props.chat!,
              //   props.messages,
              // )
              // router.push('/select-msg')
            }"
          />
        </div>
      </div>
      <div class="p-4">
        <div class="storyline flex justify-between items-center" @click="isStorylineModalOpen = true">
          <div class="flex justify-between items-center gap-1">
            <img :src="Logo" alt="">
            <div v-if="(previousUniverse?.items.length || 0) + (nextUniverse?.items.length || 0) > 0">
              Sekai Storyline ·
              Episodes {{ (previousUniverse?.items.length || 0) + (nextUniverse?.items.length || 0)
              }}
            </div>
            <div v-else>
              Make Your Own Next Episode
            </div>
          </div>
          <up-arrow-icon />
        </div>
      </div>
    </div>
    <story-line-modal
      :is-open="isStorylineModalOpen" :previous-universe="previousUniverse" :next-universe="nextUniverse"
      :presenting-element="messageAreaEl"
      @select="() => { isDownloadModalOpen = true; isStorylineModalOpen = false; downloadSekaiModalTitle = '' }" @close="isStorylineModalOpen = false"
    />
    <download-sekai-modal
      :title="downloadSekaiModalTitle" :is-modal-open="isDownloadModalOpen"
      @dismiss-modal="() => dismissModal()"
    />
    <div class="pb-unsafe-area" />
  </div>
</template>

<style scoped>
.gradient-bg {
  background: linear-gradient(180deg, rgba(21, 21, 21, 0.05), #000 89.93%);
}

.responsing {
  background: rgba(41, 41, 41, 0.5);
  backdrop-filter: blur(12.5px);
}

.tags {
  max-width: 70%;
}

ion-input.custom {
  --color: #fff;
}

.input-bg {
  background: rgba(0, 0, 0, 0.48);
}

.input-bg-2 {
  background: rgba(0, 0, 0, 0.24);
}

.creator-name {
  color: var(--white-trans-white-100, #FFF);
  font-family: Manrope;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  /* 142.857% */
}

.storyline {
  display: flex;
  padding: 16px 12px 16px 16px;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 0;
  border-radius: 100px;
  background: var(--Color-Semantic-Transparent-labelTertiary, rgba(235, 235, 247, 0.30));
  backdrop-filter: blur(10px);
  color: var(--white-trans-white-100, #FFF);
  font-family: Manrope;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  /* 142.857% */
}
</style>
